import type { ReactElement } from 'react'

import { Navigate, useSearchParams } from 'react-router-dom'
import { useIsMobile } from '../hooks/useIsMobile'
import { isAffiliate } from '../services/api/affiliates'
import { updatePlatformHeader } from '../services/api/auth'
import { setPlatformForLogs } from '../services/datadog'
import { environment, environmentVariables } from '../utils/environmentVariables'
import { redirectToUrl } from '../utils/location'
import { QueryParameters } from '../utils/queryParameters'
import { BaseSignInRoute } from './BaseSignInRoute'

type SignInContentProps = {
  searchParams: URLSearchParams
  platform: string
}

const SignInContent = ({ searchParams, platform }: SignInContentProps): ReactElement => {
  const originParamUrl = (): string | null => {
    const originParam = searchParams.get(QueryParameters.ORIGIN)
    try {
      if (originParam) {
        if (
          new URL(originParam).hostname.endsWith('swile.co') ||
          (environment === 'development' && new URL(originParam).hostname === 'localhost')
        ) {
          return originParam
        }

        console.error(`Trying to redirect to unauthorized url: '${originParam}'`)
      }
    } catch (typeError) {
      console.error(`Trying to redirect to invalid url: '${originParam}'`)
    }
    return null
  }

  const redirectionAfterSignInUrl = async (accessToken: string): Promise<string> => {
    const originParamUrlValue = originParamUrl()
    if (originParamUrlValue) {
      return originParamUrlValue
    }
    const userIsAffiliate = await isAffiliate(accessToken)
    if (userIsAffiliate) {
      return environmentVariables('AFFILIATES_APP_URL')
    }
    return environmentVariables('EMPLOYEE_APP_URL')
  }

  return BaseSignInRoute(async (accessToken) => {
    redirectToUrl(await redirectionAfterSignInUrl(accessToken))
  }, platform)
}

export const SignInRoute = (): ReactElement => {
  const [searchParams] = useSearchParams()
  const isMobileScreen = useIsMobile()
  const bypassMobileRedirect = searchParams.get('bypass_mobile') === 'true'
  const platform = 'web'

  updatePlatformHeader(platform)
  setPlatformForLogs(platform)

  // All hooks called before conditional rendering to avoid hook violations
  return isMobileScreen && !bypassMobileRedirect ? (
    <Navigate to={'/signin/mobile'} replace />
  ) : (
    <SignInContent searchParams={searchParams} platform={platform} />
  )
}
